import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Chip,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  Hub as HubIcon,
  NavigateNext as NavigateNextIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { hasReadPermission } from '../../utils/menuFilter';
import { Avatar } from '@mui/material';
import { getAssociateLedger, getAssociates, getCommissionBySponsor } from '../../services/associate';

const AssociateTransactionHistory = () => {
  const { id } = useParams();
  const history = useHistory();
  const canRead = hasReadPermission('associate.associate-list') || hasReadPermission('associate') || hasReadPermission('associate-list');
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState([]);
  const [associateInfo, setAssociateInfo] = useState(null);
  const [totalCommissionEarned, setTotalCommissionEarned] = useState(0);
  const [error, setError] = useState('');

  const formatDateOnly = (v) => {
    if (!v) return 'N/A';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  };

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch commissions from real API
      const result = await getCommissionBySponsor(id);
      if (result.success) {
        // Map API response to our table format if necessary or use directly
        // The API returns { success, totalCommissionEarned, totalRecords, data }
        setHistoryData(result.data || []);
        setTotalCommissionEarned(result.totalCommissionEarned || 0);

        // Try to get associate info if the first record has it
        if (result.data && result.data.length > 0) {
          setAssociateInfo({
            name: result.data[0].associateName || 'Associate',
            _id: id
          });
        }
      } else {
        setError('Failed to load transaction history');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while fetching history');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Associate Transaction History. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              history.push('/app/dashboard');
            }}
          >
            Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              history.push('/app/users/AssociateList');
            }}
          >
            Associates
          </Link>
          <Typography color="text.primary">Transaction History</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => history.goBack()} color="primary" sx={{ bgcolor: 'white', boxShadow: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon fontSize="large" sx={{ color: '#f57c00' }} />
              Transaction History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {associateInfo ? `${associateInfo.name} (${associateInfo.associateId || associateInfo._id})` : 'Associate Ledger Details'}
            </Typography>
          </Box>
        </Box>
        {/* <Button
          variant="outlined"
          startIcon={<HubIcon />}
          onClick={() => history.push('/app/users/AssociateTree')}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          View Hierarchy
        </Button> */}
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ bgcolor: 'white', borderRadius: 3, boxShadow: 1, borderLeft: '6px solid #f57c00' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>Total Commission Earned</Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#f57c00' }}>
                ₹{(totalCommissionEarned || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : error ? (
        <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>{error}</Alert>
      ) : (
        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden' }}>
          <CardContent sx={{ p: 0 }}>
            {historyData.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography variant="h6" color="text.secondary">No transactions found for this associate.</Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={() => history.goBack()}>Go Back</Button>
              </Box>
            ) : (
              <TableContainer>
                <Table sx={{ minWidth: 800 }}>
                  <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Booking No</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Associate</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Commission (%)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Receipt Amount</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Earned Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Remark</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyData.map((row, index) => (
                      <TableRow key={row._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, bgcolor: index % 2 === 0 ? 'inherit' : '#fafafa' }}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateOnly(row.receivedDate)}</TableCell>
                        <TableCell>
                          <Chip
                            label="Approve"
                            size="small"
                            color="success"
                            sx={{ fontWeight: 700, minWidth: 65 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.bookingId ? (
                            <Typography
                              component="span"
                              onClick={() => history.push(`/app/booking/BookingDetails/${row.bookingId}`)}
                              sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontWeight: 600,
                                '&:hover': { color: 'primary.dark' }
                              }}
                            >
                              {row.bookingNumber || row.bookingId}
                            </Typography>
                          ) : (
                            row.bookingNumber || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {row.associateName || 'Self'}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.commissionPercent ? `${row.commissionPercent}%` : '-'}</TableCell>
                        <TableCell align="right">₹{(row.receiptAmount || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900, color: 'primary.main', fontSize: '1.05rem' }}>
                          ₹{(row.commissionAmount || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.receiptNumber ? `Receipt #${row.receiptNumber}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AssociateTransactionHistory;
